// src/services/feedback-login.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const FEEDBACK_API_URL = process.env.REACT_APP_FEEDBACK_API_URL || 'https://api.jmkinfosoft.com';

const FeedbackLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Use the correct endpoint with /api prefix
    const loginUrl = `${FEEDBACK_API_URL}/api/login`;
    console.log('🔵 Login URL:', loginUrl);

    try {
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();
      console.log('🟢 Response:', data);

      if (response.ok && data.success) {
        // ✅ Set feedback specific items
        localStorage.setItem('feedbackToken', data.token);
        localStorage.setItem('feedbackUser', JSON.stringify(data.user));
        localStorage.setItem('isFeedbackLoggedIn', 'true');
        
        // ✅ ALSO set general auth items for protected route
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userRole', data.user?.role || 'admin');
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('token', data.token); // Also set token for consistency
        
        console.log('✅ Login successful, redirecting to /admin');
        console.log('📦 localStorage items set:', {
          isLoggedIn: localStorage.getItem('isLoggedIn'),
          isFeedbackLoggedIn: localStorage.getItem('isFeedbackLoggedIn'),
          userRole: localStorage.getItem('userRole'),
          hasToken: !!localStorage.getItem('token')
        });
        
        // ✅ Use navigate with replace to prevent back button issues
        navigate('/feedbackadmin', { replace: true });
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      console.error('🔴 Login Error:', err);
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>📝 Feedback Admin Login</h2>
        
        {error && (
          <div style={styles.error}>
            ❌ {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Username</label>
            <input
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={styles.input}
              required
            />
          </div>
          
          <div style={styles.inputGroup}>
            <label style={styles.label}>Password</label>
            <div style={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeButton}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          
          <button 
            type="submit" 
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {})
            }} 
            disabled={loading}
          >
            {loading ? '⏳ Logging in...' : '🚀 Login to Feedback'}
          </button>
        </form>

        <div style={styles.footer}>
          <div style={styles.demoCreds}>
            <strong>Demo Credentials:</strong><br />
            Username: admin<br />
            Password: admin123
          </div>
          <div style={{ marginTop: '15px' }}>
            <button
              onClick={() => navigate('/login')}
              style={styles.switchButton}
            >
              Switch to Recruitment Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// TEAL COLOR SCHEME
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
    padding: '20px'
  },
  card: {
    background: 'white',
    padding: '40px',
    borderRadius: '10px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
    width: '100%',
    maxWidth: '420px'
  },
  title: {
    textAlign: 'center',
    marginBottom: '30px',
    color: '#0d9488',
    fontSize: '28px',
    fontWeight: 'bold'
  },
  error: {
    background: '#fee',
    color: '#c00',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '20px',
    textAlign: 'center',
    border: '1px solid #fcc'
  },
  inputGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '600',
    color: '#333',
    fontSize: '14px'
  },
  passwordWrapper: {
    position: 'relative'
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '16px',
    boxSizing: 'border-box',
    transition: 'border-color 0.3s'
  },
  eyeButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '5px'
  },
  button: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    marginTop: '10px'
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  footer: {
    marginTop: '20px',
    textAlign: 'center'
  },
  demoCreds: {
    fontSize: '13px',
    color: '#666',
    background: '#f5f5f5',
    padding: '10px',
    borderRadius: '6px',
    marginTop: '15px',
    border: '1px solid #e0e0e0'
  },
  switchButton: {
    background: 'none',
    border: 'none',
    color: '#0d9488',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '5px 10px'
  }
};

export default FeedbackLogin;